import { useState } from 'react';
import {
  signIn,
  confirmSignIn,
  type SignInOutput,
} from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Shield } from 'lucide-react';

type Phase =
  | 'password'
  | 'mfa_pick'
  | 'mfa_code'
  | 'totp_setup'
  | 'new_password';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const [phase, setPhase] = useState<Phase>('password');
  const [challengeCode, setChallengeCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [allowedMfa, setAllowedMfa] = useState<
    ('SMS' | 'TOTP' | 'EMAIL')[]
  >([]);
  const [mfaKind, setMfaKind] = useState<
    'totp' | 'sms' | 'email' | null
  >(null);
  const [codeDestination, setCodeDestination] = useState<string | null>(
    null,
  );
  const [totpSetupUri, setTotpSetupUri] = useState<string | null>(null);
  const [totpSharedSecret, setTotpSharedSecret] = useState<string | null>(null);

  async function finishIfDone(result: SignInOutput) {
    if (result.isSignedIn && result.nextStep.signInStep === 'DONE') {
      toast.success('Signed in');
      navigate('/', { replace: true });
      return true;
    }
    return false;
  }

  async function handleNextStep(result: SignInOutput) {
    if (await finishIfDone(result)) return;

    const next = result.nextStep;
    const step = next.signInStep;

    switch (step) {
      case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
      case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION': {
        const allowed = next.allowedMFATypes?.length
          ? next.allowedMFATypes
          : (['TOTP', 'SMS'] as const);
        setAllowedMfa(allowed as ('SMS' | 'TOTP' | 'EMAIL')[]);
        setPhase('mfa_pick');
        toast.message(
          step === 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION'
            ? 'Choose how to set up two-factor authentication.'
            : 'Choose how to verify your sign-in.',
        );
        return;
      }

      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        setMfaKind('totp');
        setPhase('mfa_code');
        setChallengeCode('');
        toast.message('Enter the code from your authenticator app.');
        return;

      case 'CONFIRM_SIGN_IN_WITH_SMS_CODE': {
        setMfaKind('sms');
        setCodeDestination(next.codeDeliveryDetails?.destination ?? null);
        setPhase('mfa_code');
        setChallengeCode('');
        toast.message('Enter the code sent to your phone.');
        return;
      }

      case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE': {
        setMfaKind('email');
        setCodeDestination(next.codeDeliveryDetails?.destination ?? null);
        setPhase('mfa_code');
        setChallengeCode('');
        toast.message('Enter the code sent to your email.');
        return;
      }

      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
        const uri = next.totpSetupDetails.getSetupUri(
          'Challenge Accepted CMS',
          email.trim(),
        );
        setTotpSetupUri(uri.toString());
        setTotpSharedSecret(next.totpSetupDetails.sharedSecret ?? null);
        setPhase('totp_setup');
        setChallengeCode('');
        toast.message(
          'Add the account in your authenticator app, then enter the 6-digit code.',
        );
        return;
      }

      case 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP':
        setPhase('mfa_code');
        setMfaKind('email');
        setChallengeCode('');
        toast.message('Enter the email address to use for MFA.');
        return;

      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        setPhase('new_password');
        setNewPassword('');
        toast.message('Set a new password to continue.');
        return;

      case 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE':
        setPhase('mfa_code');
        setMfaKind(null);
        setChallengeCode('');
        toast.message('Enter the verification code.');
        return;

      default:
        toast.error(
          `Unsupported sign-in step: ${step}. Contact an administrator.`,
        );
    }
  }

  async function onSubmitPassword(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await signIn({
        username: email.trim(),
        password,
      });
      await handleNextStep(result);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function onPickMfa(method: 'TOTP' | 'SMS' | 'EMAIL') {
    setLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse: method });
      await handleNextStep(result);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function onSubmitChallenge(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = challengeCode.trim();
    if (!trimmed) {
      toast.error('Enter the code.');
      return;
    }
    setLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse: trimmed });
      await handleNextStep(result);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function onSubmitTotpSetup(e: React.FormEvent) {
    e.preventDefault();
    await onSubmitChallenge(e);
  }

  async function onSubmitNewPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!newPassword) {
      toast.error('Enter a new password.');
      return;
    }
    setLoading(true);
    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword,
        options: { userAttributes: {} },
      });
      await handleNextStep(result);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  function backToPassword() {
    setPhase('password');
    setChallengeCode('');
    setNewPassword('');
    setTotpSetupUri(null);
    setTotpSharedSecret(null);
    setMfaKind(null);
    setCodeDestination(null);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-950 px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/95 p-8 shadow-2xl backdrop-blur">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand text-white shadow-lg">
            {phase === 'password' ? (
              <Lock className="h-5 w-5" />
            ) : (
              <Shield className="h-5 w-5" />
            )}
          </div>
          <div>
            <h1 className="text-xl font-semibold text-ink">Challenge CMS</h1>
            <p className="text-sm text-ink-muted">
              {phase === 'password'
                ? 'Sign in with your Cognito user'
                : 'Complete sign-in'}
            </p>
          </div>
        </div>

        {phase === 'password' && (
          <form onSubmit={onSubmitPassword} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="mb-1 block text-sm font-medium text-ink"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none ring-brand/20 focus:border-brand focus:ring-2"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="mb-1 block text-sm font-medium text-ink"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none ring-brand/20 focus:border-brand focus:ring-2"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-brand py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-emerald-700 disabled:opacity-60"
            >
              {loading ? 'Signing in…' : 'Continue'}
            </button>
          </form>
        )}

        {phase === 'mfa_pick' && (
          <div className="space-y-4">
            <p className="text-sm text-ink-muted">
              Your account requires two-factor authentication. Choose a method:
            </p>
            <div className="flex flex-col gap-2">
              {allowedMfa.includes('TOTP') && (
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => onPickMfa('TOTP')}
                  className="rounded-lg border border-slate-200 bg-white py-3 text-sm font-medium text-ink hover:bg-slate-50 disabled:opacity-60"
                >
                  Authenticator app (TOTP)
                </button>
              )}
              {allowedMfa.includes('SMS') && (
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => onPickMfa('SMS')}
                  className="rounded-lg border border-slate-200 bg-white py-3 text-sm font-medium text-ink hover:bg-slate-50 disabled:opacity-60"
                >
                  Text message (SMS)
                </button>
              )}
              {allowedMfa.includes('EMAIL') && (
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => onPickMfa('EMAIL')}
                  className="rounded-lg border border-slate-200 bg-white py-3 text-sm font-medium text-ink hover:bg-slate-50 disabled:opacity-60"
                >
                  Email
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={backToPassword}
              className="w-full text-sm text-ink-muted underline"
            >
              Back
            </button>
          </div>
        )}

        {phase === 'mfa_code' && (
          <form onSubmit={onSubmitChallenge} className="space-y-4">
            {codeDestination && (
              <p className="text-xs text-ink-muted">
                Sent to: {codeDestination}
              </p>
            )}
            <div>
              <label
                htmlFor="challenge"
                className="mb-1 block text-sm font-medium text-ink"
              >
                {mfaKind === 'totp' && 'Authenticator code'}
                {mfaKind === 'sms' && 'SMS code'}
                {mfaKind === 'email' && 'Email code'}
                {!mfaKind && 'Verification code'}
              </label>
              <input
                id="challenge"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={challengeCode}
                onChange={(e) => setChallengeCode(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm tracking-widest outline-none ring-brand/20 focus:border-brand focus:ring-2"
                placeholder="••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-brand py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-emerald-700 disabled:opacity-60"
            >
              {loading ? 'Verifying…' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={backToPassword}
              className="w-full text-sm text-ink-muted underline"
            >
              Back
            </button>
          </form>
        )}

        {phase === 'totp_setup' && totpSetupUri && (
          <form onSubmit={onSubmitTotpSetup} className="space-y-4">
            <p className="text-sm text-ink-muted">
              On your phone, open your authenticator app and add this account (or
              tap the link on mobile). Then enter the rotating code below.
            </p>
            <a
              href={totpSetupUri}
              className="block break-all text-sm font-medium text-brand hover:underline"
            >
              Add to authenticator app
            </a>
            {totpSharedSecret && (
              <p className="rounded-lg bg-slate-100 px-3 py-2 font-mono text-xs text-ink">
                Manual entry secret: {totpSharedSecret}
              </p>
            )}
            <input
              type="text"
              inputMode="numeric"
              value={challengeCode}
              onChange={(e) => setChallengeCode(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm outline-none focus:border-brand focus:ring-2"
              placeholder="6-digit code"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-brand py-2.5 text-sm font-semibold text-white disabled:opacity-60"
            >
              {loading ? 'Verifying…' : 'Confirm'}
            </button>
            <button
              type="button"
              onClick={backToPassword}
              className="w-full text-sm text-ink-muted underline"
            >
              Back
            </button>
          </form>
        )}

        {phase === 'new_password' && (
          <form onSubmit={onSubmitNewPassword} className="space-y-4">
            <p className="text-sm text-ink-muted">
              Your account requires a new password.
            </p>
            <div>
              <label
                htmlFor="newpass"
                className="mb-1 block text-sm font-medium text-ink"
              >
                New password
              </label>
              <input
                id="newpass"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-brand py-2.5 text-sm font-semibold text-white disabled:opacity-60"
            >
              {loading ? 'Saving…' : 'Set password'}
            </button>
            <button
              type="button"
              onClick={backToPassword}
              className="w-full text-sm text-ink-muted underline"
            >
              Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
